// src/hooks/useAppointmentsPG.js
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Appointment } from '../models';
import { AppointmentAPI } from '../services/api/appointmentAPI';
import { useAuth } from '../context/AuthContext';
import { cacheManager } from '../services/cacheManager';
import { getErrorMessage } from '../utils/errorMessages';

// Week-based cache management utilities
const CACHE_PREFIX = 'salon_appointments_cache_week_';
const CACHE_METADATA_KEY = 'salon_appointments_cache_metadata';
const DASHBOARD_METRICS_CACHE_KEY = 'salon_dashboard_metrics_cache';
const DASHBOARD_METRICS_CACHE_DURATION = 1 * 60 * 60 * 1000; // 1 hour in milliseconds
const MONTHLY_SUMMARY_CACHE_KEY = 'salon_monthly_appointments_summary_cache';
const MONTHLY_SUMMARY_CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

// Get Sunday of the week containing the given date
const getWeekStart = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day;
  return new Date(d.setDate(diff)).toISOString().split('T')[0];
};

// Get Saturday of the week containing the given date
const getWeekEnd = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + 6;
  return new Date(d.setDate(diff)).toISOString().split('T')[0];
};

// Get all week start dates between two dates
const getWeeksBetween = (startDate, endDate) => {
  const weeks = [];

  let currentWeekStart = getWeekStart(startDate);
  const endWeekStart = getWeekStart(endDate);

  while (currentWeekStart <= endWeekStart) {
    weeks.push(currentWeekStart);
    const nextWeek = new Date(currentWeekStart);
    nextWeek.setDate(nextWeek.getDate() + 7);
    currentWeekStart = nextWeek.toISOString().split('T')[0];
  }

  return weeks;
};

// Load cached weeks metadata
const getCachedWeeks = () => {
  try {
    const metadata = localStorage.getItem(CACHE_METADATA_KEY);
    return metadata ? JSON.parse(metadata) : [];
  } catch (error) {
    console.error('Error loading cache metadata:', error);
    return [];
  }
};

// Save cached weeks metadata
const saveCachedWeeks = (weeks) => {
  try {
    localStorage.setItem(CACHE_METADATA_KEY, JSON.stringify(weeks));
  } catch (error) {
    console.error('Error saving cache metadata:', error);
  }
};

// Get cached appointments for a specific week
const getCachedWeek = (weekStart) => {
  try {
    const key = `${CACHE_PREFIX}${weekStart}`;
    const cached = localStorage.getItem(key);
    if (cached) {
      const data = JSON.parse(cached);
      return data.appointments;
    }
    return null;
  } catch (error) {
    console.error('Error loading cached week:', error);
    return null;
  }
};

// Invalidate cache for a specific week
const invalidateWeek = (weekStart) => {
  try {
    const key = `${CACHE_PREFIX}${weekStart}`;
    localStorage.removeItem(key);

    // Update metadata
    const cachedWeeks = getCachedWeeks();
    const updatedWeeks = cachedWeeks.filter(week => week !== weekStart);
    saveCachedWeeks(updatedWeeks);
  } catch (error) {
    console.error('Error invalidating week:', error);
  }
};

// Invalidate all weeks that contain a specific date
export const invalidateCacheForDate = async (date) => {
  const weekStart = getWeekStart(date);
  invalidateWeek(weekStart);
  await cacheManager.invalidateCache('appointments');
};

// Clear all appointments cache (used when services are updated)
export const clearAllAppointmentsCache = async () => {
  try {
    // Get all cached weeks
    const cachedWeeks = getCachedWeeks();

    // Remove all week caches
    cachedWeeks.forEach(weekStart => {
      const key = `${CACHE_PREFIX}${weekStart}`;
      localStorage.removeItem(key);
    });

    // Clear metadata
    localStorage.removeItem(CACHE_METADATA_KEY);

    // Also clear dashboard metrics cache
    localStorage.removeItem(DASHBOARD_METRICS_CACHE_KEY);

    // Invalidate via cacheManager
    await cacheManager.invalidateCache('appointments');

    console.log('Cleared all appointments cache', { weekCount: cachedWeeks.length });
  } catch (error) {
    console.error('Error clearing all appointments cache:', error);
  }
};

export function useAppointmentsPG() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentDateRange, setCurrentDateRange] = useState({
    start: null,
    end: null
  });
  const [pendingAppointments, setPendingAppointments] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);

  // Clear error
  const clearError = useCallback(() => setError(null), []);

  const { user } = useAuth();

  // Load appointments by date range with week-based caching
  const loadingRanges = useRef(new Set());

  // Cache utility functions (add these if they don't exist)
  const getCachedAppointments = (weekStart) => {
    try {
      const cached = localStorage.getItem(`${CACHE_PREFIX}${weekStart}`);
      return cached ? JSON.parse(cached) : null;
    } catch (error) {
      console.error('Error getting cached appointments:', error);
      return null;
    }
  };

  const getPendingApprovals = useCallback(async () => {
    try {
      console.log('Hook: Getting pending approval appointments');
      setError(null);
      setPendingLoading(true);

      const pending = await AppointmentAPI.getPendingApprovals();
      setPendingAppointments(pending);

      pending.forEach(appointment => {
        invalidateCacheForDate(appointment.date);
      });

      console.log('Hook: Successfully retrieved pending appointments', {
        count: pending.length
      });

      return pending;
    } catch (error) {
      console.error('Hook: Error getting pending appointments:', error);
      setError(error.message);
      throw error;
    } finally {
      setPendingLoading(false);
    }
  }, []);

  // Get cached dashboard metrics
  const getCachedDashboardMetrics = () => {
    try {
      const cached = localStorage.getItem(DASHBOARD_METRICS_CACHE_KEY);
      if (!cached) return null;

      const data = JSON.parse(cached);
      const now = new Date().getTime();
      const cacheTime = new Date(data.timestamp).getTime();

      // Check if cache is expired (older than 1 hour)
      if (now - cacheTime > DASHBOARD_METRICS_CACHE_DURATION) {
        console.log('Dashboard metrics cache expired, clearing');
        localStorage.removeItem(DASHBOARD_METRICS_CACHE_KEY);
        return null;
      }

      console.log('Using cached dashboard metrics', {
        cachedAt: data.timestamp,
        ageMinutes: Math.round((now - cacheTime) / (1000 * 60))
      });

      return data.metrics;
    } catch (error) {
      console.error('Error loading cached dashboard metrics:', error);
      localStorage.removeItem(DASHBOARD_METRICS_CACHE_KEY);
      return null;
    }
  };

  // Set cached dashboard metrics
  const setCachedDashboardMetrics = (metrics) => {
    try {
      const cacheData = {
        metrics,
        timestamp: new Date().toISOString()
      };

      localStorage.setItem(DASHBOARD_METRICS_CACHE_KEY, JSON.stringify(cacheData));

      console.log('Cached dashboard metrics', {
        timestamp: cacheData.timestamp,
        todayAppointments: metrics.today?.total_appointments,
        weekRevenue: metrics.this_week?.revenue
      });
    } catch (error) {
      console.error('Error caching dashboard metrics:', error);
    }
  };

  // Clear dashboard metrics cache (optional utility function)
  const clearDashboardMetricsCache = () => {
    try {
      localStorage.removeItem(DASHBOARD_METRICS_CACHE_KEY);
      console.log('Dashboard metrics cache cleared');
    } catch (error) {
      console.error('Error clearing dashboard metrics cache:', error);
    }
  };

  // Invalidate dashboard metrics cache when appointments change
  const invalidateDashboardMetricsCache = () => {
    console.log('Invalidating dashboard metrics cache due to appointment changes');
    clearDashboardMetricsCache();
  };

  // Replace the existing getDashboardMetrics function with this cached version:
  const getDashboardMetrics = useCallback(async (forceRefresh) => {
    try {
      console.log('Hook: Getting dashboard metrics');
      setError(null);

      // Check for cached data first
      const cachedMetrics = getCachedDashboardMetrics();
      if (!forceRefresh && cachedMetrics) {
        console.log('Hook: Returning cached dashboard metrics');
        return cachedMetrics;
      }

      // Fetch fresh data if no valid cache
      console.log('Hook: Fetching fresh dashboard metrics from API');
      const metrics = await AppointmentAPI.getDashboardMetrics();

      // Cache the fresh data
      setCachedDashboardMetrics(metrics);

      console.log('Hook: Successfully retrieved and cached dashboard metrics', {
        todayAppointments: metrics.today.total_appointments,
        weekRevenue: metrics.this_week.revenue,
        monthRevenue: metrics.this_month.revenue
      });

      return metrics;

    } catch (err) {
      console.error('Hook: Error getting dashboard metrics:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  const getOverdueAppointments = useCallback(async () => {
    try {
      console.log('Hook: Getting getOverdueAppointments');
      setError(null);
      setLoading(true);

      const overdueAppointments = await AppointmentAPI.getOverdueAppointments();

      console.log('Hook: Successfully retrieved overdueAppointments', {
        overdueAppointments
      });

      return overdueAppointments;
    } catch (error) {
      console.error('Hook: Error getting overdueAppointments:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const getUnpaidAppointments = useCallback(async () => {
    try {
      console.log('Hook: Getting getUnpaidAppointments');
      setError(null);
      setLoading(true);

      const unpaidAppointments = await AppointmentAPI.getUnpaidAppointments();

      console.log('Hook: Successfully retrieved unpaidAppointments', {
        unpaidAppointments
      });

      return unpaidAppointments;
    } catch (error) {
      console.error('Hook: Error getting unpaidAppointments:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const getMonthlyRevenueSummary = useCallback(async (startMonth, endMonth) => {
    try {
      console.log('Hook: Getting monthly revenue summary', { startMonth, endMonth });
      setError(null);
      setLoading(true);

      // Use AppointmentAPI following existing pattern
      const revenueData = await AppointmentAPI.getMonthlyRevenueSummary(startMonth, endMonth);

      console.log('Hook: Successfully retrieved monthly revenue summary', {
        monthsCount: revenueData.length,
        totalRevenue: revenueData.reduce((sum, month) => sum + month.revenue, 0),
        totalUnpaid: revenueData.reduce((sum, month) => sum + month.unpaid, 0)
      });

      return revenueData;
    } catch (error) {
      console.error('Hook: Error getting monthly revenue summary:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const setCachedAppointments = (weekStart, appointments) => {
    try {
      const cacheData = {
        appointments,
        timestamp: new Date().toISOString(),
        weekStart
      };
      localStorage.setItem(`${CACHE_PREFIX}${weekStart}`, JSON.stringify(cacheData));

      // Update metadata
      const metadata = getCachedWeeks();
      if (!metadata.includes(weekStart)) {
        metadata.push(weekStart);
        localStorage.setItem(CACHE_METADATA_KEY, JSON.stringify(metadata));
      }
    } catch (error) {
      console.error('Error setting cached appointments:', error);
    }
  };

  const isCacheExpired = async (timestamp) => {
    try {
      const cacheTime = new Date(timestamp).getTime();

      // Check Firestore invalidation first
      const isValid = await cacheManager.isCacheValid('appointments', cacheTime);
      if (!isValid) {
        console.log('Cache invalidated by Firestore timestamp');
        return true;
      }

      // Cache is valid according to Firestore
      return false;
    } catch (error) {
      return true; // Consider expired on error
    }
  };

  const loadAppointmentsByDateRange = useCallback(async (startDate, endDate, forceRefresh = false) => {
    const cacheKey = `${startDate}_${endDate}`;
    // Check if we're already loading this range
    if (loadingRanges.current.has(cacheKey)) {
      console.log('Already loading range:', cacheKey);
      return;
    }

    console.log('Loading appointments for date range:', {
      startDate,
      endDate,
      forceRefresh,
      context: forceRefresh ? 'FORCED_REFRESH' : 'NORMAL_LOAD'
    });

    // If forceRefresh is true, skip cache and go directly to API
    if (!forceRefresh) {
      // Check cache first only if not forcing refresh
      const cachedWeeks = getWeeksBetween(startDate, endDate);
      const allCached = await Promise.all(
        cachedWeeks.map(async weekStart => {
          const cached = getCachedAppointments(weekStart);
          return cached && !(await isCacheExpired(cached.timestamp));
        })
      ).then(results => results.every(r => r));

      if (allCached) {
        console.log('All weeks cached and not expired, loading from cache');
        const cachedAppointments = [];
        cachedWeeks.forEach(weekStart => {
          const cached = getCachedAppointments(weekStart);
          if (cached?.appointments) {
            cached.appointments.forEach(apt => {
              if (apt.date >= startDate && apt.date <= endDate) {
                cachedAppointments.push(apt);
              }
            });
          }
        });

        const appointmentInstances = cachedAppointments.map(apt => Appointment.fromJSON(apt));
        setAppointments(appointmentInstances);
        setCurrentDateRange({ start: startDate, end: endDate });

        console.log(`Loaded ${appointmentInstances.length} appointments from cache`);
        return;
      }
    } else {
      console.log('Force refresh requested - skipping cache check');
    }

    try {
      setLoading(true);
      setError(null);
      loadingRanges.current.add(cacheKey);

      console.log('Loading appointments from API:', { startDate, endDate });

      // Use the API to get fresh data
      const rawAppointments = await AppointmentAPI.getByDateRange(startDate, endDate);

      console.log('API returned appointments:', rawAppointments.length);

      // Convert to Appointment instances
      const appointmentInstances = rawAppointments.map(apt => Appointment.fromJSON(apt));

      // Cache the results by week (always update cache with fresh data)
      const appointmentsByWeek = {};
      appointmentInstances.forEach(apt => {
        const weekStart = getWeekStart(apt.date);
        if (!appointmentsByWeek[weekStart]) {
          appointmentsByWeek[weekStart] = [];
        }
        appointmentsByWeek[weekStart].push(apt.toJSON());
      });

      // Save to cache - this will overwrite any existing cache with fresh data
      Object.entries(appointmentsByWeek).forEach(([weekStart, weekAppointments]) => {
        setCachedAppointments(weekStart, weekAppointments);
        console.log(`Updated cache for week ${weekStart} with ${weekAppointments.length} appointments`);
      });

      // Handle weeks with no appointments - clear their cache if they exist
      const weeksInRange = getWeeksBetween(startDate, endDate);
      weeksInRange.forEach(weekStart => {
        if (!appointmentsByWeek[weekStart]) {
          // This week has no appointments, cache empty array
          setCachedAppointments(weekStart, []);
          console.log(`Cached empty array for week ${weekStart}`);
        }
      });

      // Update state
      setAppointments(appointmentInstances);
      setCurrentDateRange({ start: startDate, end: endDate });

      console.log(`Successfully loaded and cached ${appointmentInstances.length} appointments from API`);

    } catch (err) {
      console.error('Error loading appointments by date range:', err);
      setError(`Failed to load appointments: ${err.message}`);

      // Try to load from cache as fallback only if not force refresh
      if (!forceRefresh) {
        const cachedWeeks = getWeeksBetween(startDate, endDate);
        const cachedAppointments = [];
        cachedWeeks.forEach(weekStart => {
          const cached = getCachedAppointments(weekStart);
          if (cached?.appointments) {
            cached.appointments.forEach(apt => {
              if (apt.date >= startDate && apt.date <= endDate) {
                cachedAppointments.push(apt);
              }
            });
          }
        });

        if (cachedAppointments.length > 0) {
          console.log('Using cached appointments as fallback:', cachedAppointments.length);
          const appointmentInstances = cachedAppointments.map(apt => Appointment.fromJSON(apt));
          setAppointments(appointmentInstances);
          setError(`Using cached data. ${err.message}`);
        }
      }

    } finally {
      setLoading(false);
      loadingRanges.current.delete(cacheKey);
    }
  }, []);

  // Combine regular appointments with pending appointments for complete view
  const allAppointments = useMemo(() => {
    const combined = [...appointments];

    // Add pending appointments that aren't already in the regular appointments array
    pendingAppointments.forEach(pendingApt => {
      const exists = appointments.find(apt => apt.id === pendingApt.id);
      if (!exists) {
        combined.push(pendingApt);
      }
    });

    // Sort by date and time
    return combined.sort((a, b) => {
      const dateCompare = a.date.localeCompare(b.date);
      return dateCompare === 0 ? a.time.localeCompare(b.time) : dateCompare;
    });
  }, [appointments, pendingAppointments]);

  // Add a force refresh function for convenience
  const forceRefreshCurrentRange = useCallback(async () => {
    if (currentDateRange.start && currentDateRange.end) {
      console.log('Force refreshing current date range');
      await loadAppointmentsByDateRange(currentDateRange.start, currentDateRange.end, true);
    }
  }, [currentDateRange, loadAppointmentsByDateRange]);

  const addAppointment = useCallback(async (appointmentData) => {
    try {
      console.log('Hook: Adding new appointment', { appointmentData });
      setLoading(true);
      setError(null);

      // Transform field names to match API expectations
      const apiAppointmentData = {
        ...appointmentData,
        // Map frontend field names to API field names
        date: appointmentData.date || appointmentData.date,
        time: appointmentData.time || appointmentData.time,
      };

      if (user?.uid) {
        apiAppointmentData.userId = user.uid;
      }

      // Remove the old field names if they exist
      if (appointmentData.date && !appointmentData.date) {
        delete apiAppointmentData.date;
      }
      if (appointmentData.time && !appointmentData.time) {
        delete apiAppointmentData.time;
      }

      console.log('Hook: Transformed appointment data for API', { apiAppointmentData });

      // Use the API to create the appointment
      const apiResult = await AppointmentAPI.create(apiAppointmentData);

      // Handle both single appointment and recurring series
      const isRecurringSeries = apiResult.isRecurring && Array.isArray(apiResult.appointments);

      if (isRecurringSeries) {
        console.log('Hook: Recurring appointment series created successfully', {
          appointmentCount: apiResult.appointmentCount,
          seriesId: apiResult.appointments[0]?.recurring_series_id
        });

        // Convert all appointments to instances
        const appointmentInstances = apiResult.appointments.map(apt => Appointment.fromJSON(apt));

        // Add all appointments to current appointments state
        setAppointments(prevAppointments => {
          const updatedAppointments = [...prevAppointments, ...appointmentInstances];

          // Sort appointments by date and time
          return updatedAppointments.sort((a, b) => {
            const dateCompare = a.date.localeCompare(b.date);
            return dateCompare === 0 ?
              a.time.localeCompare(b.time) : dateCompare;
          });
        });
        await cacheManager.invalidateCache('appointments');
        return appointmentInstances;
      } else {
        // Single appointment created
        const createdAppointment = apiResult;

        console.log('Hook: Single appointment created successfully', {
          appointmentId: createdAppointment.id,
          date: createdAppointment.date
        });

        // Convert to Appointment instance
        const appointmentInstance = Appointment.fromJSON(createdAppointment);

        // Add to current appointments state
        setAppointments(prevAppointments => {
          const updatedAppointments = [...prevAppointments, appointmentInstance];

          // Sort appointments by date and time
          return updatedAppointments.sort((a, b) => {
            const dateCompare = a.date.localeCompare(b.date);
            return dateCompare === 0 ?
              a.time.localeCompare(b.time) : dateCompare;
          });
        });

        return appointmentInstance;
      }

    } catch (err) {
      console.error('Hook: Error adding appointment:', err);

      // Get user-friendly error message (pass details for context-specific messages)
      const errorInfo = err.errorCode
        ? getErrorMessage(err.errorCode, err.details)
        : { message: err.message || 'שגיאה ביצירת התור' };

      setError(errorInfo.message);

      // Preserve error structure through the chain
      const enhancedError = new Error(errorInfo.message);
      enhancedError.errorCode = err.errorCode;
      enhancedError.details = err.details;
      throw enhancedError;
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  // Also add this validation helper function for the hook:
  const validateAppointmentData = useCallback((appointmentData) => {
    const errors = [];

    // Basic required field validation (client-side)
    // Check for both possible field name formats
    if (!appointmentData.customer_name && !appointmentData.customer_id) {
      errors.push('נדרש לבחור לקוח');
    }
    // Check for services - supports both single service and multi-service format
    if (!appointmentData.service_name && !appointmentData.service_id && (!appointmentData.services || appointmentData.services.length === 0)) {
      errors.push('נדרש להזין את סוג השירות לתור');
    }
    if (!appointmentData.crew_member_name && !appointmentData.crew_member_id) {
      errors.push('נדרש לבחור איש צוות מבצע');
    }

    // Check for both date field formats: 'date' or 'date'
    if (!appointmentData.date && !appointmentData.date) {
      errors.push('נדרש להזין את תאריך התור');
    }

    // Check for both time field formats: 'time' or 'time'
    if (!appointmentData.time && !appointmentData.time) {
      errors.push('נדרש להזין את זמן התור');
    }

    if (!appointmentData.duration) {
      errors.push('נדרש להזין את משך זמן התור');
    }
    if (appointmentData.price === undefined || appointmentData.price === null) {
      errors.push('נדרש להזין את מחיר התור');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }, []);

  const validateAppointmentUpdates = useCallback((updates) => {
    const errors = [];
    const warnings = [];

    // Validate date format if provided
    if (updates.date || updates.date) {
      const dateValue = updates.date || updates.date;
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(dateValue)) {
        errors.push('Date must be in YYYY-MM-DD format');
      }
    }

    // Validate time format if provided
    if (updates.time || updates.time) {
      const timeValue = updates.time || updates.time;
      const timeRegex = /^\d{2}:\d{2}$/;
      if (!timeRegex.test(timeValue)) {
        errors.push('Time must be in HH:MM format');
      }
    }

    // Validate duration if provided
    if (updates.duration && (updates.duration < 1 || updates.duration > 720)) {
      errors.push('משך זמן התור חייב להיות לפחות דקה ולא יותר מ-12 שעות ');
    }

    // Validate price if provided
    if (updates.price !== undefined && updates.price < 0) {
      errors.push('מחיר השירות לא יכול להיות שלילי');
    }

    // Validate status if provided
    const validStatuses = ['scheduled', 'confirmed', 'completed', 'cancelled', 'no_show', 'no_show_justified', 'pending_approval'];
    if (updates.status && !validStatuses.includes(updates.status)) {
      errors.push(`Status must be one of: ${validStatuses.join(', ')}`);
    }

    // Check for past appointments warning
    if ((updates.date || updates.date) && (updates.time || updates.time)) {
      const dateValue = updates.date || updates.date;
      const timeValue = updates.time || updates.time;
      const appointmentDateTime = new Date(`${dateValue}T${timeValue}`);
      const now = new Date();

      if (appointmentDateTime < now) {
        warnings.push('התור נקבע בדיעבד לזמן שכבר עבר');
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }, []);

  const updateAppointment = useCallback(async (appointmentId, updates) => {
    try {
      console.log('Hook: Updating appointment', { appointmentId, updates });

      if (!appointmentId) {
        throw new Error('Appointment ID is required');
      }

      if (!updates || Object.keys(updates).length === 0) {
        throw new Error('Updates object is required and cannot be empty');
      }

      // Find the appointment in current state
      const currentAppointment = appointments.find(apt => apt.id === appointmentId);
      if (!currentAppointment) {
        console.warn('Hook: Appointment not found in current state', { appointmentId });
      }

      // FIXED: Clean field transformation - only include fields that have actual values
      const apiUpdates = {};

      // Copy all non-date/time fields directly
      Object.keys(updates).forEach(key => {
        if (key !== 'date' && key !== 'time' && updates[key] !== undefined && updates[key] !== null && updates[key] !== '') {
          apiUpdates[key] = updates[key];
        }
      });

      // FIXED: Only map date/time fields if they have actual values (not undefined/null/empty)
      if (updates.date && updates.date !== '' && updates.date !== null && updates.date !== undefined) {
        apiUpdates.date = updates.date;
      }

      if (updates.time && updates.time !== '' && updates.time !== null && updates.time !== undefined) {
        apiUpdates.time = updates.time;
      }

      // Also handle direct API field names
      if (updates.date && updates.date !== '' && updates.date !== null && updates.date !== undefined) {
        apiUpdates.date = updates.date;
      }

      if (updates.time && updates.time !== '' && updates.time !== null && updates.time !== undefined) {
        apiUpdates.time = updates.time;
      }

      console.log('Hook: Transformed updates for API', {
        originalUpdates: updates,
        apiUpdates,
        hasDateChange: !!apiUpdates.date,
        hasTimeChange: !!apiUpdates.time
      });

      // Use the API to update the appointment
      const response = await AppointmentAPI.update(appointmentId, apiUpdates);

      // Rest of the response handling logic remains the same...
      let updatedAppointment;

      if (response && response._automationResults) {
        const { _automationResults, ...appointmentData } = response;
        updatedAppointment = appointmentData;

        console.log('Hook: Received response with automation results', {
          appointmentId: updatedAppointment.id,
          automationResults: _automationResults
        });
      } else if (response && response.id) {
        updatedAppointment = response;

        console.log('Hook: Received regular appointment response', {
          appointmentId: updatedAppointment.id,
          status: updatedAppointment.status
        });
      } else if (response === null) {
        console.warn('Hook: Received null response - constructing from current state and updates');

        if (currentAppointment) {
          updatedAppointment = {
            ...currentAppointment,
            ...updates,
            // Handle field name mapping
            ...(updates.date && {
              date: updates.date,
              date: updates.date
            }),
            ...(updates.time && {
              time: updates.time,
              time: updates.time
            }),
            updated_at: new Date().toISOString()
          };

          console.log('Hook: Constructed appointment data from current state', {
            appointmentId: updatedAppointment.id,
            updatedFields: Object.keys(updates)
          });
        } else {
          throw new Error('Cannot update appointment: null response and no cached data');
        }
      } else {
        console.error('Hook: Unexpected response format', { response });
        throw new Error('Invalid response format from server');
      }

      console.log('Hook: Appointment updated successfully', {
        appointmentId: updatedAppointment.id,
        customerName: updatedAppointment.customer_name,
        date: updatedAppointment.date || updatedAppointment.date,
        time: updatedAppointment.time || updatedAppointment.time,
        status: updatedAppointment.status
      });
      await cacheManager.invalidateCache('appointments');

      // Convert to Appointment instance
      // Handle series vs single update for state management
      if (response.isRecurringSeries && response.updatedAppointments) {
        // Series update - update all appointments in state
        const updatedInstances = response.updatedAppointments.map(apt => Appointment.fromJSON(apt));

        setAppointments(prevAppointments => {
          const updatedIds = response.updatedAppointments.map(apt => apt.id);
          const otherAppointments = prevAppointments.filter(apt => !updatedIds.includes(apt.id));
          const allAppointments = [...otherAppointments, ...updatedInstances];

          return allAppointments.sort((a, b) => {
            const dateCompare = a.date.localeCompare(b.date);
            return dateCompare === 0 ? a.time.localeCompare(b.time) : dateCompare;
          });
        });

        console.log('Hook: Updated recurring series in state', {
          seriesId: response.updatedAppointments[0]?.recurring_series_id,
          updatedCount: response.updatedAppointments.length
        });

        // Return the response as-is for cache invalidation handling
        return response;
      } else {
        // Single appointment update
        const appointmentInstance = Appointment.fromJSON(updatedAppointment);

        setAppointments(prevAppointments => {
          const updatedAppointments = prevAppointments.map(apt =>
            apt.id === appointmentId ? appointmentInstance : apt
          );

          return updatedAppointments.sort((a, b) => {
            const dateCompare = a.date.localeCompare(b.date);
            return dateCompare === 0 ? a.time.localeCompare(b.time) : dateCompare;
          });
        });

        return appointmentInstance;
      }

    } catch (err) {
      console.error('Hook: Error updating appointment:', {
        appointmentId,
        updates,
        error: err.message
      });

      const errorMessage = `עדכון הפגישה נכשל. אנא נסה שוב.
פרטי השגיאה: ${err.message}`;
      setError(errorMessage);
      throw new Error(errorMessage);

    } finally {
      setLoading(false);
    }
  }, [appointments]);

  const deleteAppointment = useCallback(async (appointmentId, deleteMode = 'single') => {
    try {
      console.log('Hook: Deleting appointment', { appointmentId, deleteMode });
      setLoading(true);
      setError(null);

      // Validate appointmentId
      if (!appointmentId) {
        throw new Error('Appointment ID is required');
      }

      // Find the appointment in current state (for cache cleanup)
      const appointmentToDelete = appointments.find(apt => apt.id === appointmentId);
      if (!appointmentToDelete) {
        console.warn('Hook: Appointment not found in current state', { appointmentId });
      }

      // Use the API to delete the appointment with deleteMode
      const deleteResult = await AppointmentAPI.delete(appointmentId, deleteMode);

      console.log('Hook: Appointment deleted successfully', {
        appointmentId: deleteResult.appointmentId || appointmentId,
        deleteMode,
        deletedCount: deleteResult.deletedCount || 1,
        customerName: deleteResult.deletedAppointment?.customer_name || deleteResult.deletedAppointments?.[0]?.customer_name
      });

      // Handle cache invalidation for both single and series deletions
      if (deleteResult.deletedAppointments && Array.isArray(deleteResult.deletedAppointments)) {
        // Recurring series deletion - invalidate cache for all deleted appointment dates
        deleteResult.deletedAppointments.forEach(deletedApt => {
          invalidateCacheForDate(deletedApt.date);
        });
        await cacheManager.invalidateCache('appointments');

        // Remove all deleted appointments from state
        setAppointments(prevAppointments => {
          const deletedIds = deleteResult.deletedAppointments.map(apt => apt.id);
          const updatedAppointments = prevAppointments.filter(apt => !deletedIds.includes(apt.id));
          console.log('Hook: Removed recurring series from state', {
            previousCount: prevAppointments.length,
            newCount: updatedAppointments.length,
            deletedCount: deletedIds.length
          });
          return updatedAppointments;
        });

        console.log('Hook: Invalidated cache for recurring series deletion', {
          deletedCount: deleteResult.deletedAppointments.length,
          dates: deleteResult.deletedAppointments.map(apt => apt.date)
        });
      } else {
        // Single appointment deletion
        if (appointmentToDelete) {
          invalidateCacheForDate(appointmentToDelete.date);
        }
        await cacheManager.invalidateCache('appointments');

        // Remove from current appointments state
        setAppointments(prevAppointments => {
          const updatedAppointments = prevAppointments.filter(apt => apt.id !== appointmentId);
          console.log('Hook: Removed single appointment from state', {
            previousCount: prevAppointments.length,
            newCount: updatedAppointments.length
          });
          return updatedAppointments;
        });
      }

      // Update cache by removing the appointment(s)
      if (deleteResult.deletedAppointments && Array.isArray(deleteResult.deletedAppointments)) {
        // Handle multiple appointments (recurring series)
        deleteResult.deletedAppointments.forEach(deletedApt => {
          const weekStart = getWeekStart(deletedApt.date);
          const cachedWeek = getCachedAppointments(weekStart);
          if (cachedWeek) {
            const updatedCachedAppointments = cachedWeek.appointments.filter(
              apt => apt.id !== deletedApt.id
            );
            setCachedAppointments(weekStart, updatedCachedAppointments);
          }
        });
        console.log('Hook: Removed recurring series from cache');
      } else if (appointmentToDelete) {
        // Handle single appointment
        const weekStart = getWeekStart(appointmentToDelete.date);
        const cachedWeek = getCachedAppointments(weekStart);
        if (cachedWeek) {
          const updatedCachedAppointments = cachedWeek.appointments.filter(
            apt => apt.id !== appointmentId
          );
          setCachedAppointments(weekStart, updatedCachedAppointments);
          console.log('Hook: Removed single appointment from cache');
        }
      }

      invalidateDashboardMetricsCache();
      await cacheManager.invalidateCache('appointments');
      console.log('Hook: Successfully deleted appointment(s) from state and cache');
      return deleteResult;

    } catch (err) {
      console.error('Hook: Error deleting appointment', {
        appointmentId,
        error: err.message,
        stack: err.stack
      });

      const errorMessage = `חלה שגיאה במחיקת התור, אנא נסה שנית. פרטי השגיאה: ${err.message}`;
      setError(errorMessage);
      throw new Error(errorMessage);

    } finally {
      setLoading(false);
    }
  }, [appointments]);

  const deleteAppointmentWithConfirmation = useCallback(async (appointmentId, deleteMode = 'single', skipConfirmation = true) => {
    try {
      // Show confirmation dialog unless skipped
      if (!skipConfirmation) {
        const confirmMessage = `Are you sure you want to permanently delete this appointment?`;

        const confirmed = window.confirm(confirmMessage);
        if (!confirmed) {
          console.log('Hook: Appointment deletion cancelled by user');
          return { cancelled: true };
        }
      }

      // Proceed with deletion, passing through deleteMode
      const result = await deleteAppointment(appointmentId, deleteMode);
      return { cancelled: false, ...result };

    } catch (err) {
      console.error('Hook: Error in delete confirmation flow', err);
      throw err;
    }
  }, [appointments, deleteAppointment]);

  const getAppointment = useCallback(async (appointmentId, forceRefresh = false) => {
    try {
      console.log('Hook: Getting appointment by ID', { appointmentId });
      setError(null);

      // Validate appointment ID
      if (!appointmentId) {
        throw new Error('Appointment ID is required');
      }

      // First check if appointment is already in current state (cache)
      const cachedAppointment = appointments.find(apt => apt.id === appointmentId);
      if (!forceRefresh && cachedAppointment) {
        console.log('Hook: Returning cached appointment', { appointmentId });
        return cachedAppointment;
      }

      // If not in cache, fetch from API
      console.log('Hook: Fetching appointment from API', { appointmentId });
      const appointmentData = await AppointmentAPI.getById(appointmentId);

      if (!appointmentData) {
        console.log('Hook: Appointment not found', { appointmentId });
        return null;
      }

      // Convert to Appointment instance
      const appointmentInstance = Appointment.fromJSON(appointmentData);

      console.log('Hook: Successfully retrieved appointment by ID', {
        appointmentId: appointmentInstance.id,
        customerName: appointmentInstance.customer_name,
        date: appointmentInstance.date,
        time: appointmentInstance.time
      });

      // Optionally add to current appointments state if within loaded date range
      // This helps keep the cache fresh
      setAppointments(prevAppointments => {
        const exists = prevAppointments.find(apt => apt.id === appointmentId);
        if (!exists) {
          // Add to state and maintain sort order
          const updatedAppointments = [...prevAppointments, appointmentInstance];
          return updatedAppointments.sort((a, b) => {
            const dateCompare = a.date.localeCompare(b.date);
            return dateCompare === 0 ? a.time.localeCompare(b.time) : dateCompare;
          });
        }
        return prevAppointments;
      });

      return appointmentInstance;

    } catch (err) {
      console.error('Hook: Error getting appointment by ID:', {
        appointmentId,
        error: err.message
      });
      setError(err.message);
      throw err;
    }
  }, [appointments]);

  const handleAppointmentSubmit = useCallback(async (appointmentData, mode = 'add') => {
    try {
      console.log('Hook: Handling appointment submit', { appointmentData, mode });
      // Remove updated_at if present (server-managed)
      delete appointmentData.updated_at;
      if (mode === 'add') {
        // Validate data first
        const validation = validateAppointmentData(appointmentData);
        if (!validation.isValid) {
          throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
        }

        // Create the appointment
        const newAppointment = await addAppointment(appointmentData);

        // Invalidate cache for all created appointments
        if (Array.isArray(newAppointment)) {
          // Recurring series - invalidate cache for all appointment dates
          newAppointment.forEach(apt => {
            invalidateCacheForDate(apt.date);
          });
          console.log('Hook: Invalidated cache for recurring series', {
            appointmentCount: newAppointment.length,
            dates: newAppointment.map(apt => apt.date)
          });
        } else {
          // Single appointment
          invalidateCacheForDate(appointmentData.date || appointmentData.date);
        }

        console.log('Hook: Appointment submit completed successfully');
        return newAppointment;

      } else if (mode === 'edit') {
        // Handle edit mode
        const appointmentId = appointmentData.id;
        if (!appointmentId) {
          throw new Error('Appointment ID is required for edit mode');
        }

        // Extract updates (exclude id from updates)
        const { id, ...updates } = appointmentData;

        // Validate updates
        const validation = validateAppointmentUpdates(updates);
        if (!validation.isValid) {
          throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
        }

        // Show warnings if any
        if (validation.warnings.length > 0) {
          console.warn('Hook: Update validation warnings:', validation.warnings);
        }

        // Update the appointment
        const updateResult = await updateAppointment(appointmentId, updates);
        console.log('Hook: Appointment update completed successfully');

        // Handle both single and series update cache invalidation
        if (updateResult.isRecurringSeries && updateResult.updatedAppointments) {
          // Recurring series update - invalidate cache for all updated appointment dates
          updateResult.updatedAppointments.forEach(updatedApt => {
            invalidateCacheForDate(updatedApt.date);
          });
          console.log('Hook: Invalidated cache for recurring series update', {
            updatedCount: updateResult.updatedAppointments.length,
            dates: updateResult.updatedAppointments.map(apt => apt.date)
          });

          // Return the main appointment for consistency
          return updateResult.mainAppointment;
        } else {
          // Single appointment update
          invalidateCacheForDate(appointmentData.date || appointmentData.date);
          return updateResult;
        }
      } else if (mode === 'delete') {
        // Handle delete mode
        const appointmentId = appointmentData.id || appointmentData.appointmentId;
        if (!appointmentId) {
          throw new Error('Appointment ID is required for delete mode');
        }

        const deleteMode = appointmentData.deleteMode || 'single';
        const result = await deleteAppointmentWithConfirmation(appointmentId, deleteMode);
        console.log('Hook: Appointment delete completed successfully');

        // Invalidate cache for all deleted appointments
        if (result.deletedAppointments && Array.isArray(result.deletedAppointments)) {
          // Recurring series deletion - invalidate cache for all deleted appointment dates
          result.deletedAppointments.forEach(deletedApt => {
            invalidateCacheForDate(deletedApt.date);
          });
          console.log('Hook: Invalidated cache for recurring series deletion', {
            deletedCount: result.deletedAppointments.length,
            dates: result.deletedAppointments.map(apt => apt.date)
          });
        } else {
          // Single appointment deletion
          invalidateCacheForDate(appointmentData.date || appointmentData.date);
        }

        return result;

      } else {
        throw new Error(`Unknown mode: ${mode}`);
      }

    } catch (err) {
      console.error('Hook: Error in appointment submit', {
        appointmentData,
        mode,
        error: err.message
      });
      throw err;
    }
  }, [addAppointment, updateAppointment, validateAppointmentData, validateAppointmentUpdates, deleteAppointmentWithConfirmation]);

  const getRecentAppointments = useCallback(async () => {
    try {
      console.log('Hook: Getting today\'s appointments from API');
      setError(null);

      const appointments = await AppointmentAPI.getTodayAppointments();
      const appointmentInstances = appointments.map(apt => Appointment.fromJSON(apt));

      console.log('Hook: Successfully retrieved today\'s appointments', {
        count: appointmentInstances.length,
        date: new Date().toISOString().split('T')[0]
      });

      return appointmentInstances;

    } catch (err) {
      console.error('Hook: Error getting today\'s appointments:', err);
      setError(err.message);
      throw err;
    }
  }, []);


  const getUpcomingAppointments = useCallback(async (limit = 10) => {
    try {
      console.log('Hook: Getting upcoming appointments from API', { limit });
      setError(null);

      const appointments = await AppointmentAPI.getUpcomingAppointments(limit);
      const appointmentInstances = appointments.map(apt => Appointment.fromJSON(apt));

      console.log('Hook: Successfully retrieved upcoming appointments', {
        limit,
        count: appointmentInstances.length
      });

      return appointmentInstances;

    } catch (err) {
      console.error('Hook: Error getting upcoming appointments:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  const getAppointmentsByDateRange = useCallback((startDate, endDate) => {
    return appointments.filter(appointment =>
      appointment.date >= startDate && appointment.date <= endDate
    ).sort((a, b) => {
      const dateCompare = a.date.localeCompare(b.date);
      return dateCompare === 0 ? a.time.localeCompare(b.time) : dateCompare;
    });
  }, [appointments]);

  const getCustomerAppointments = useCallback(async (customerId, limit = 0) => {
    try {
      console.log('Hook: Getting customer appointments from API', {
        customerId,
        limit: limit === 0 ? 'unlimited' : limit
      });
      setError(null);

      // Validate parameters
      if (!customerId) {
        throw new Error('Customer ID is required');
      }

      // Validate UUID format
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(customerId)) {
        throw new Error('Invalid customer ID format');
      }

      // Validate limit (0 means no limit)
      if (typeof limit !== 'number' || limit < 0 || limit > 200) {
        throw new Error('Limit must be a number between 0 and 200 (0 means no limit)');
      }

      // Get appointments from API
      const appointments = await AppointmentAPI.getCustomerAppointments(customerId, limit);
      const appointmentInstances = appointments.map(apt => Appointment.fromJSON(apt));

      console.log('Hook: Successfully retrieved customer appointments', {
        customerId,
        limit: limit === 0 ? 'unlimited' : limit,
        count: appointmentInstances.length,
        customerName: appointmentInstances[0]?.customer_name || 'Unknown'
      });

      return appointmentInstances;

    } catch (err) {
      console.error('Hook: Error getting customer appointments:', {
        customerId,
        limit: limit === 0 ? 'unlimited' : limit,
        error: err.message
      });
      setError(err.message);
      throw err;
    }
  }, []);

  // Computed values (using current loaded appointments)
  const todayAppointments = useMemo(() => {
    const today = new Date().toLocaleDateString('en-CA'); // "YYYY-MM-DD"

    return appointments
      .filter(apt => {
        const aptDate = new Date(apt.date).toLocaleDateString('en-CA'); // normalize
        return aptDate === today && apt.status !== 'cancelled';
      })
      .sort((a, b) => a.time.localeCompare(b.time));
  }, [appointments]);

  const upcomingAppointments = useMemo(() => {
    const now = new Date();

    return appointments
      .filter(apt => {
        const aptDate = new Date(apt.date);
        // Optional: overwrite time if `apt.time` exists separately
        if (apt.time) {
          const [hours, minutes] = apt.time.split(':').map(Number);
          aptDate.setHours(hours, minutes, 0, 0);
        }

        return aptDate > now && apt.status !== 'cancelled';
      })
      .sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);

        if (a.time) {
          const [hA, mA] = a.time.split(':').map(Number);
          dateA.setHours(hA, mA, 0, 0);
        }

        if (b.time) {
          const [hB, mB] = b.time.split(':').map(Number);
          dateB.setHours(hB, mB, 0, 0);
        }

        return dateA.getTime() - dateB.getTime();
      });
  }, [appointments]);

  // Load initial appointments on mount
  useEffect(() => {
    const loadInitialAppointments = async () => {
      // Get current week's date range
      const today = new Date();
      const weekStart = getWeekStart(today.toISOString().split('T')[0]);
      const weekEnd = getWeekEnd(today.toISOString().split('T')[0]);

      // Try to load from cache first
      const cachedWeek = getCachedWeek(weekStart);
      if (cachedWeek && cachedWeek.length > 0) {
        const appointmentInstances = cachedWeek.map(apt => Appointment.fromJSON(apt));
        setAppointments(appointmentInstances);
        setCurrentDateRange({ start: weekStart, end: weekEnd });
      } else {
        // Load from database if cache is empty
        await loadAppointmentsByDateRange(weekStart, weekEnd);
      }
    };
    loadInitialAppointments();
  }, []); // Empty dependency array - only run once on mount

  // Helper functions for monthly summary cache
  const getCachedMonthlySummary = () => {
    try {
      const cached = localStorage.getItem(MONTHLY_SUMMARY_CACHE_KEY);
      if (!cached) return null;

      const { data, timestamp } = JSON.parse(cached);
      const now = Date.now();

      // Check if cache is expired (24 hours)
      if (now - timestamp > MONTHLY_SUMMARY_CACHE_DURATION) {
        console.log('Monthly summary cache expired, clearing...');
        localStorage.removeItem(MONTHLY_SUMMARY_CACHE_KEY);
        return null;
      }

      console.log('Monthly summary cache hit', {
        age: Math.round((now - timestamp) / (60 * 60 * 1000) * 10) / 10, // Hours with 1 decimal
        monthsCount: data.length
      });

      return data;
    } catch (error) {
      console.warn('Error reading monthly summary cache:', error);
      localStorage.removeItem(MONTHLY_SUMMARY_CACHE_KEY);
      return null;
    }
  };

  const setCachedMonthlySummary = (data) => {
    try {
      const cacheData = {
        data,
        timestamp: Date.now()
      };
      localStorage.setItem(MONTHLY_SUMMARY_CACHE_KEY, JSON.stringify(cacheData));
      console.log('Monthly summary cached successfully', {
        monthsCount: data.length,
        expiresAt: new Date(Date.now() + MONTHLY_SUMMARY_CACHE_DURATION).toLocaleString()
      });
    } catch (error) {
      console.warn('Error caching monthly summary:', error);
    }
  };

  const clearMonthlySummaryCache = () => {
    try {
      localStorage.removeItem(MONTHLY_SUMMARY_CACHE_KEY);
      console.log('Monthly summary cache cleared');
    } catch (error) {
      console.warn('Error clearing monthly summary cache:', error);
    }
  };

  const getMonthlyAppointmentsSummary = useCallback(async (forceRefresh = false) => {
    try {
      console.log('Hook: Getting monthly appointments summary for chart', { forceRefresh });
      setError(null);

      // Check cache first unless force refresh is requested
      if (!forceRefresh) {
        const cachedData = getCachedMonthlySummary();
        if (cachedData) {
          console.log('Hook: Returning cached monthly appointments summary');
          return cachedData;
        }
      }

      console.log('Hook: Fetching fresh monthly appointments summary from API');

      // Get raw data from API
      const rawData = await AppointmentAPI.getMonthlyAppointmentsSummary();

      // Transform and validate data for chart component
      const chartData = rawData.map(item => ({
        date: item.date, // Should already be in YYYY-MM-DD format
        value: parseInt(item.value, 10) // Ensure value is an integer
      })).filter(item => {
        // Filter out any invalid data points
        const isValidDate = /^\d{4}-\d{2}-\d{2}$/.test(item.date);
        const isValidValue = !isNaN(item.value) && item.value >= 0;

        if (!isValidDate || !isValidValue) {
          console.warn('Hook: Filtering out invalid data point:', item);
          return false;
        }

        return true;
      });

      // Cache the processed data
      setCachedMonthlySummary(chartData);

      console.log('Hook: Successfully retrieved and processed monthly appointments summary', {
        monthsCount: chartData.length,
        totalAppointments: chartData.reduce((sum, month) => sum + month.value, 0),
        dateRange: chartData.length > 0 ?
          `${chartData[0].date} to ${chartData[chartData.length - 1].date}` :
          'No data'
      });

      return chartData;

    } catch (err) {
      console.error('Hook: Error getting monthly appointments summary:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  /**
 * Get available appointment slots for a service (NO CACHE ACCESS)
 * This method deliberately avoids any local state/cache access for privacy
 * @param {string} serviceId - Service ID
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD) 
 * @param {Object} options - Additional options
 * @returns {Promise<Array>} Array of available slots by date
 */
  const getAvailableSlots = useCallback(async (serviceId, startDate, endDate, options = {}) => {
    try {
      console.log('Hook: Getting available slots from API (privacy-safe)', {
        serviceId: serviceId?.substring(0, 8) + '***', // Mask ID for privacy
        startDate,
        endDate,
        options: {
          ...options,
          crewMemberId: options.crewMemberId?.substring(0, 8) + '***' // Mask crew ID
        }
      });

      setError(null);

      // Validate parameters
      if (!serviceId) {
        throw new Error('Service ID is required');
      }

      if (!startDate || !endDate) {
        throw new Error('Both startDate and endDate are required');
      }

      // Validate date format
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(startDate) || !dateRegex.test(endDate)) {
        throw new Error('Dates must be in YYYY-MM-DD format');
      }

      // Validate date range
      if (new Date(startDate) > new Date(endDate)) {
        throw new Error('startDate cannot be after endDate');
      }

      // 🚨 PRIVACY PROTECTION: Direct API call only - NO local state access
      // This ensures we don't load or expose appointment data from other customers
      const availableSlots = await AppointmentAPI.getAvailableSlots(
        serviceId,
        startDate,
        endDate,
        options
      );

      console.log('Hook: Successfully retrieved available slots (privacy-safe)', {
        serviceId: serviceId?.substring(0, 8) + '***',
        startDate,
        endDate,
        datesWithSlots: availableSlots.filter(day => day.totalSlots > 0).length,
        totalSlots: availableSlots.reduce((sum, day) => sum + day.totalSlots, 0),
        crewMembersFound: [...new Set(
          availableSlots.flatMap(day =>
            day.availableSlots.map(slot => slot.crewMemberId)
          )
        )].length
      });

      return availableSlots;

    } catch (err) {
      console.error('Hook: Error getting available slots (privacy-safe):', {
        serviceId: serviceId?.substring(0, 8) + '***',
        startDate,
        endDate,
        error: err.message
      });
      setError(err.message);
      throw err;
    }
  }, []);

  const ensureDateRangeLoaded = useCallback(async (targetDate) => {
    // Check if this date is already in our loaded range
    if (currentDateRange.start && currentDateRange.end &&
      targetDate >= currentDateRange.start && targetDate <= currentDateRange.end) {
      return; // Already loaded
    }

    // Load the week containing this date
    const weekStart = getWeekStart(targetDate);
    const weekEnd = getWeekEnd(targetDate);

    await loadAppointmentsByDateRange(weekStart, weekEnd, true);
  }, [currentDateRange, loadAppointmentsByDateRange]);


  return {
    // State
    appointments,
    loading,
    error,
    clearError,
    allAppointments,

    // Data loading
    loadAppointmentsByDateRange,
    forceRefreshCurrentRange,
    invalidateCacheForDate,
    getMonthlyAppointmentsSummary,
    clearMonthlySummaryCache,
    getPendingApprovals,
    getMonthlyRevenueSummary,
    getOverdueAppointments,
    getUnpaidAppointments,
    ensureDateRangeLoaded,

    // CRUD operations
    addAppointment,
    updateAppointment,
    deleteAppointment,
    getAppointment,
    handleAppointmentSubmit,
    invalidateDashboardMetricsCache,

    // List operations
    appointmentInstances: appointments,
    getRecentAppointments,
    getUpcomingAppointments,
    getAppointmentsByDateRange,

    // Customer-specific
    getCustomerAppointments,
    getAvailableSlots,

    // Dashboard
    getDashboardMetrics,
    todayAppointments,
    upcomingAppointments,
    pendingAppointments,
    pendingLoading,
  };
}


/**
 * Custom hook for monthly service distribution data
 * @returns {Object} Hook state and actions
 */
export function useMonthlyServiceDistribution() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  const fetchData = useCallback(async (force = false) => {
    // Skip if already loading or recently fetched (unless forced)
    if (loading || (!force && lastFetched && Date.now() - lastFetched < 60000)) {
      return;
    }

    try {
      console.log('Hook: Fetching monthly service distribution data');
      setLoading(true);
      setError(null);

      const distributionData = await AppointmentAPI.getMonthlyServiceDistribution();

      console.log('Hook: Successfully loaded monthly service distribution', {
        monthsCount: [...new Set(distributionData.map(row => row.month))].length,
        servicesCount: [...new Set(distributionData.map(row => row.service_name))].length,
        totalRecords: distributionData.length
      });

      setData(distributionData);
      setLastFetched(Date.now());

    } catch (err) {
      console.error('Hook: Error loading monthly service distribution:', err);
      setError(`Failed to load service distribution data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [loading, lastFetched]);

  // Load data on mount
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Refresh function for manual updates
  const refresh = useCallback(() => {
    return fetchData(true);
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    refresh,
    lastFetched: lastFetched ? new Date(lastFetched) : null,
  };
}
